'use client';

import { YouTubeEmbed } from '@next/third-parties/google';
import { useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ResultOf, readFragment, type FragmentOf } from '@/utils/graphql';
import { videoSelectorFragment } from './videoSelectorFragment';

function VideoItem({
  video,
}: {
  video: ResultOf<typeof videoSelectorFragment>;
}) {
  if (!video) {
    return null;
  }

  switch (video.provider) {
    case 'youtube':
      const params = video.start_at ? `start=${video.start_at}` : undefined;

      return <YouTubeEmbed videoid={video.identifier} params={params} />;
    default:
      return `Unsupported video provider: ${video.provider}`;
  }
}

function MultipleVideos({
  videos,
}: {
  videos: ResultOf<typeof videoSelectorFragment>[];
}) {
  const [selectedVideo, setSelectedVideo] = useState(videos[0]);

  const handleVideoChange = (newVideoId: string) => {
    const video = videos.find((video) => video?.id === newVideoId)!;
    setSelectedVideo(video);
  };

  return (
    <div className="mb-6">
      <div className="py-2 px-4 md:px-8 lg:px-0 font-semibold border-b border-border">
        <Select onValueChange={handleVideoChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue
              placeholder={selectedVideo?.type?.translations?.[0]?.name}
            />
          </SelectTrigger>
          <SelectContent>
            {videos.map(
              (video, index) =>
                video && (
                  <SelectItem key={video.id} value={video.id}>
                    {video.type?.translations?.[0]?.name ||
                      `Vídeo ${index + 1}`}
                  </SelectItem>
                ),
            )}
          </SelectContent>
        </Select>
      </div>

      <VideoItem video={selectedVideo} />
    </div>
  );
}

export function VideoSelector({
  videos: _videos,
}: {
  videos: (FragmentOf<typeof videoSelectorFragment> | null)[] | null;
}) {
  if (!_videos?.length) {
    return;
  }

  const videos = _videos.map((video) =>
    readFragment(videoSelectorFragment, video!),
  );

  if (videos.length === 1 && videos[0]) {
    const video = videos[0];

    return (
      <div className="mb-6">
        {video.type?.translations?.[0]?.name && (
          <h2 className="py-3 px-4 md:px-8 font-semibold border-b border-border">
            {video.type.translations[0].name}
          </h2>
        )}
        <VideoItem video={video} />
      </div>
    );
  }

  return <MultipleVideos videos={videos} />;
}
